import type { GraphNode, WorkflowGraph } from "./workflowGraph";

/**
 * The graph seen one level at a time: the nodes drawn inside each frame, and every edge as
 * an edge between two nodes of one level. An edge from a step inside an open sub-workflow
 * counts as an edge from the sub-workflow at the outer level. Used by the layout and by the
 * list view.
 */
export interface GraphLevels {
  /** Nodes per parent (`null`: the top level), in graph order. */
  readonly childrenOf: (parentId: string | null) => readonly GraphNode[];
  /** Source and target pairs at the level inside `parentId`, without repeats. */
  readonly edgesAt: (parentId: string | null) => readonly (readonly [string, string])[];
}

export function graphLevels(graph: WorkflowGraph): GraphLevels {
  const children = new Map<string | null, GraphNode[]>();
  for (const node of graph.nodes) {
    children.set(node.parentId, [...(children.get(node.parentId) ?? []), node]);
  }
  const parentOf = new Map(graph.nodes.map((node) => [node.id, node.parentId]));

  const ancestorAt = (id: string, level: string | null): string | null => {
    let current: string | null = id;
    while (current !== null) {
      const parent = parentOf.get(current);
      if (parent === undefined) {
        return null;
      }
      if (parent === level) {
        return current;
      }
      current = parent;
    }
    return null;
  };

  return {
    childrenOf: (parentId) => children.get(parentId) ?? [],
    edgesAt: (parentId) => {
      const pairs = new Map<string, readonly [string, string]>();
      for (const edge of graph.edges) {
        const source = ancestorAt(edge.source, parentId);
        const target = ancestorAt(edge.target, parentId);
        if (source !== null && target !== null && source !== target) {
          pairs.set(`${source}\n${target}`, [source, target]);
        }
      }
      return [...pairs.values()];
    },
  };
}
